import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import caretDownUrl from '../../assets/signals/caret-down.svg';
import type { SignalsQuery } from '../../lib/signals.hooks.js';
import {
  completenessOf,
  groupSignals,
  SIGNAL_GROUPINGS,
  type SignalGrouping,
  type SignalTab,
  signalCounts,
  signalsIn,
} from '../../lib/signals.js';
import { SIGNALS_SECTION_ID } from '../../lib/signals-search.js';
import { SignalRow } from './SignalRow.js';
import { SignalsButtonLink } from './SignalsLink.js';

const PAGE_SIZE = 5;

const TAB_LABELS: Record<SignalTab, string> = {
  new: 'New',
  high: 'High severity',
  active: 'All active',
  dismissed: 'Dismissed',
};

const GROUPING_LABELS: Record<SignalGrouping, string> = {
  none: 'None',
  workflow: 'Workflow',
  account: 'Account',
};

const EMPTY_COPY: Record<SignalTab, string> = {
  new: 'No new signals. Everything flagged has been opened.',
  high: 'No high-severity signals right now.',
  active: 'You’re all caught up. Alison keeps watching your live creatives.',
  dismissed: 'No dismissed signals.',
};

const INCOMPLETE_COPY =
  'Nothing active is listed, but not every signal could be read. Retry to check again.';

interface SignalsSectionProps {
  query: SignalsQuery;
  tab: SignalTab;
  grouping: SignalGrouping;
  onTabChange: (tab: SignalTab) => void;
  onGroupingChange: (grouping: SignalGrouping) => void;
}

function CaretIcon(props: { className?: string }): ReactNode {
  return (
    <Box
      component="img"
      src={caretDownUrl}
      alt=""
      width={16}
      height={16}
      className={props.className}
      sx={{ right: '8px !important', top: 'calc(50% - 8px) !important' }}
    />
  );
}

function TabButton({
  tab,
  count,
  selected,
  onSelect,
}: {
  tab: SignalTab;
  count: number;
  selected: boolean;
  onSelect: () => void;
}): ReactNode {
  return (
    <ButtonBase
      role="tab"
      aria-selected={selected}
      onClick={onSelect}
      sx={{
        gap: '6px',
        pt: '4px',
        pb: '10px',
        mb: '-1px',
        borderBottom: '2px solid',
        borderColor: selected ? 'primary.main' : 'transparent',
        fontSize: 13,
        fontWeight: selected ? 600 : 500,
        color: selected ? 'primary.main' : 'text.secondary',
        '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main' },
      }}
    >
      {TAB_LABELS[tab]}
      <Box
        component="span"
        sx={{
          px: '6px',
          py: '1px',
          borderRadius: '8px',
          fontSize: 11,
          fontWeight: 600,
          bgcolor: selected ? 'primary.bg' : 'background.platform',
          color: selected ? 'primary.main' : 'text.secondary',
        }}
      >
        {count}
      </Box>
    </ButtonBase>
  );
}

export function SignalsSection({
  query,
  tab,
  grouping,
  onTabChange,
  onGroupingChange,
}: SignalsSectionProps): ReactNode {
  const [shownCount, setShownCount] = useState<number>(PAGE_SIZE);
  const { state } = query;
  const counts = signalCounts(query.signals, state);
  const listed = signalsIn(tab, query.signals, state);
  const visible = listed.slice(0, shownCount);
  const groups = groupSignals(visible, grouping);
  const incomplete =
    query.feed !== null && completenessOf(query.feed, counts.active) === 'incomplete';
  const selectTab = (next: SignalTab): void => {
    setShownCount(PAGE_SIZE);
    onTabChange(next);
  };

  return (
    <Stack
      component="section"
      id={SIGNALS_SECTION_ID}
      aria-labelledby="signals-section-title"
      sx={{ gap: '16px', scrollMarginTop: '16px' }}
    >
      <Stack direction="row" sx={{ alignItems: 'flex-start', gap: '16px' }}>
        <Stack sx={{ flex: 1, gap: '2px' }}>
          <Typography
            id="signals-section-title"
            component="h2"
            tabIndex={-1}
            sx={{ fontSize: 16, fontWeight: 600, color: 'background.icon' }}
          >
            Signals
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            Flagged from your live portfolio — act on one or dismiss it
          </Typography>
        </Stack>
        <SignalsButtonLink
          search={{ signalsTab: 'active' }}
          sx={{ p: 0, minWidth: 0, fontSize: 13, fontWeight: 600, textTransform: 'none' }}
        >
          View all signals →
        </SignalsButtonLink>
      </Stack>

      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: '24px',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" role="tablist" aria-label="Signal views" sx={{ gap: '24px' }}>
          {(['new', 'high', 'active', 'dismissed'] as const).map((key) => (
            <TabButton
              key={key}
              tab={key}
              count={counts[key]}
              selected={tab === key}
              onSelect={() => selectTab(key)}
            />
          ))}
        </Stack>
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" sx={{ alignItems: 'center', gap: '8px', pb: '6px' }}>
          <Typography
            id={'signals-group-by'}
            sx={{ fontSize: 12, fontWeight: 500, color: 'text.secondary' }}
          >
            Group by
          </Typography>
          <Select
            size="small"
            value={grouping}
            onChange={(event) => onGroupingChange(event.target.value as SignalGrouping)}
            IconComponent={CaretIcon}
            inputProps={{ 'aria-labelledby': 'signals-group-by' }}
            sx={{
              fontSize: 12,
              borderRadius: '8px',
              '& .MuiSelect-select': { p: '5px 30px 5px 10px !important', minHeight: 0 },
            }}
          >
            {SIGNAL_GROUPINGS.map((key) => (
              <MenuItem key={key} value={key} sx={{ fontSize: 12 }}>
                {GROUPING_LABELS[key]}
              </MenuItem>
            ))}
          </Select>
        </Stack>
      </Stack>

      {query.refreshFailed || (query.feed?.unreadable ?? 0) > 0 ? (
        <Alert
          severity="warning"
          data-testid="signals-partial"
          action={
            <Button color="inherit" size="small" onClick={query.retry}>
              Retry
            </Button>
          }
        >
          {query.refreshFailed
            ? 'Signals could not be refreshed; showing the last list loaded.'
            : `${query.feed?.unreadable} signal result${query.feed?.unreadable === 1 ? '' : 's'} could not be loaded.`}
        </Alert>
      ) : null}
      {query.feed?.truncated ? (
        <Alert severity="info">
          Only the most recent signal runs of the last {query.feed.windowDays} days were checked;
          older ones are not listed.
        </Alert>
      ) : null}

      {query.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: '40px' }}>
          <CircularProgress aria-label="Loading signals" />
        </Box>
      ) : query.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={query.retry}>
              Retry
            </Button>
          }
        >
          Signals could not be loaded.
        </Alert>
      ) : listed.length === 0 ? (
        <Box
          data-testid="signals-empty"
          sx={{
            p: '32px',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '14px',
            bgcolor: 'background.paper',
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            {incomplete && tab !== 'dismissed' ? INCOMPLETE_COPY : EMPTY_COPY[tab]}
          </Typography>
        </Box>
      ) : (
        groups.map((group) => (
          <Stack key={group.key} sx={{ gap: '8px' }}>
            {group.label === null ? null : (
              <Typography component="h3" sx={{ fontSize: 12, fontWeight: 600 }}>
                {group.label} · {group.signals.length}
              </Typography>
            )}
            <Box
              component="ul"
              aria-label={group.label ?? 'Signals list'}
              sx={{
                m: 0,
                p: 0,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '14px',
                overflow: 'clip',
              }}
            >
              {group.signals.map((signal) => (
                <SignalRow
                  key={signal.runId}
                  signal={signal}
                  unread={!state.read.has(signal.runId)}
                  variant="full"
                  dismissed={tab === 'dismissed'}
                  onOpen={(opened) => query.markRead(opened.runId)}
                  onDismiss={(dismissed) => query.dismiss(dismissed.runId)}
                  onRestore={(restored) => query.restore(restored.runId)}
                />
              ))}
            </Box>
          </Stack>
        ))
      )}

      {listed.length > visible.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <ButtonBase
            onClick={() => setShownCount((count) => count + PAGE_SIZE)}
            sx={{
              p: '7px 11px',
              borderRadius: '9px',
              border: '1px solid',
              borderColor: 'text.muted',
              bgcolor: 'background.paper',
              fontSize: 12,
              fontWeight: 700,
              '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main' },
            }}
          >
            Load more
          </ButtonBase>
        </Box>
      ) : null}
    </Stack>
  );
}
